import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import { Box, Modal } from "@mui/material";
import logo from "../image/dycgpl-logo.webp";
import "react-toastify/dist/ReactToastify.css";
import "./Login.css";
import axiosWithToken from "../../utils/commonComponent/axiosTokenConfig";
import { BASE_URL } from "../../global";
import { fetchRBACData, getEmployeeData } from "../Pages/rbac/hasAccess";

const COUNTRIES = [
  { name: "India",     code: "in" },
  { name: "UAE",       code: "ae" },
  { name: "USA",       code: "us" },
  { name: "UK",        code: "gb" },
  { name: "Singapore", code: "sg" },
];

// tracks wrong login attempts, blocks after 3 tries for 1 min
const recordLoginAttempt = () => {
  const count = Number(localStorage.getItem("loginCount") || 0) + 1;
  if (count > 2) localStorage.setItem("blockTimer", Date.now() + 60000);
  localStorage.setItem("loginCount", count);
};

// saves all user data to storage after successful login
const persistSession = (userData, token) => {
  sessionStorage.setItem("isLoggedIn", true);
  sessionStorage.setItem("authToken", token);
  sessionStorage.setItem("logInUser", userData.userName);
  localStorage.setItem("id",           userData.id);
  localStorage.setItem("clientId",     userData.clientId);
  localStorage.setItem("userName",     userData.userName);
  localStorage.setItem("type",         userData.type);
  localStorage.setItem("modules",      userData.modules ?? "");
  localStorage.setItem("inspectorId",  userData.employeeId);
  localStorage.setItem("roles",        userData.roles);
  localStorage.setItem("editAccess",   userData.editAccess);
  localStorage.setItem("policyStatus", userData.policyStatus);
  localStorage.setItem("policyDate",   userData.policyDate);
  localStorage.setItem("userSign",     userData.usersign);
  localStorage.setItem("employeeId",   userData.employeeId);
};

// base styles shared between both modals
const modalBaseStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  background: "rgba(255,255,255,0.15)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.25)",
  padding: "20px",
  textAlign: "center",
  borderRadius: "20px",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 8px 32px rgba(0,0,0,.15)",
};

function ForgotCredentialsModal({ open, onClose }) {
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ ...modalBaseStyle, width: 500, height: 312, justifyContent: "center", alignItems: "center" }}>
        <span style={{ fontSize: "24px" }}>
          Kindly contact your admin to reset your credentials!
        </span>
      </Box>
    </Modal>
  );
}

function SupportModal({ open, onClose }) {
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        ...modalBaseStyle,
        width: 500,
        height: "auto",
        minHeight: 200,
        background: "rgba(255,255,255,0.92)",
        gap: "10px",
        justifyContent: "center",
      }}>
        <span style={{ fontSize: "20px", fontWeight: 700, color: "#16215c" }}>🎧 Need help?</span>
        <p style={{ margin: 0, fontSize: "15px", color: "#334155" }}>Our support team is here for you.</p>
        <p style={{ margin: 0, fontSize: "15px", color: "#334155" }}>Email: <strong>support@dycglobal.com</strong></p>
        <p style={{ margin: 0, fontSize: "15px", color: "#334155" }}>Phone: <strong>+91-00000-00000</strong></p>
      </Box>
    </Modal>
  );
}

function CountryPicker({ countries, selectedIndex, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = countries[selectedIndex];

  return (
    <div className="country-picker-wrapper" ref={ref}>
      <button type="button" className="country-picker" onClick={() => setOpen((v) => !v)}>
        <span className={`flag-swatch flag-swatch--${selected.code}`} />
        {selected.name}
        <span className={`country-chevron${open ? " country-chevron--open" : ""}`}>▾</span>
      </button>

      {open && (
        <div className="country-dropdown">
          <div className="country-dropdown-list">
            {countries.map((c, i) => (
              <button
                type="button"
                key={c.code}
                className={`country-dropdown-item${i === selectedIndex ? " country-dropdown-item--active" : ""}`}
                onClick={() => { onSelect(i); setOpen(false); }}
              >
                <span className={`flag-swatch flag-swatch--${c.code}`} />
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Login({ setIsLoggedIn }) {
  const navigate = useNavigate();

  const [userName,     setUserName]     = useState("");
  const [password,     setPassword]     = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading,    setIsLoading]    = useState(false);
  const [error,        setError]        = useState("");
  const [expanded,     setExpanded]     = useState(false);
  const [countryIndex, setCountryIndex] = useState(0);
  const [forgotOpen,   setForgotOpen]   = useState(false);
  const [supportOpen,  setSupportOpen]  = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const res = await axiosWithToken.post(BASE_URL + "login", { userName, password });
      const userData = res.data?.user;

      if (res.status === 200 && userData) {
        persistSession(userData, res.data.token);
        await getEmployeeData(userData.employeeId);
        await fetchRBACData(true, null);
        setIsLoggedIn(true);
        toast.success("Login Successful!");
        navigate("/", { replace: true });
        window.location.reload(true);
      } else {
        setError("Incorrect Username / Password");
        setUserName("");
        setPassword("");
        recordLoginAttempt();
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.response?.data?.error || "An error occurred");
      toast.error("Incorrect Username / Password");
      recordLoginAttempt();
      setTimeout(() => window.location.reload(), 700);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="limiter">
        <div className={`container-login100${!expanded ? " container-login100--navbar" : ""}`}>
          <div className={`wrap-login100${!expanded ? " wrap-login100--collapsed" : ""}`}>

            {/* navbar pill shown before user clicks login */}
            {!expanded && (
              <>
                <div className="login100-brand">
                  <div className="login100-pic-collapsed">
                    <img src={logo} alt="DYC Global" />
                  </div>
                  <div className="login100-brand-text">
                    <span className="login100-brand-name">DYC GLOBAL</span>
                    <span className="login100-brand-tagline">Ensuring Sustainable Excellence</span>
                  </div>
                </div>

                <div className="login100-navbar-right">
                  <button type="button" className="navbar-quicklink" onClick={() => setSupportOpen(true)}>
                    Support
                  </button>
                  <span className="navbar-divider">|</span>
                  <CountryPicker
                    countries={COUNTRIES}
                    selectedIndex={countryIndex}
                    onSelect={setCountryIndex}
                  />
                  <span className="navbar-divider">|</span>
                  <button type="button" className="navbar-login-btn" onClick={() => setExpanded(true)}>
                    Login
                  </button>
                </div>
              </>
            )}

            {/* actual login form */}
            {expanded && (
              <>
                <button
                  type="button"
                  className="login100-back-btn"
                  aria-label="Back"
                  onClick={() => setExpanded(false)}
                >
                  <ArrowBackIcon fontSize="small" />
                </button>

                <div className="login100-pic">
                  <img src={logo} width="90%" alt="DYC Global" />
                </div>

                <div className="login100-form login100-form--animated">
                  <span className="login100-form-title">
                    Welcome To <br />
                    <span>DYC Global</span>
                  </span>

                  <div className="wrap-input100">
                    <input
                      className="input100"
                      type="text"
                      placeholder="Username"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                    />
                    <span className="focus-input100" />
                    <span className="symbol-input100">
                      <i className="fa fa-envelope" aria-hidden="true" />
                    </span>
                  </div>

                  <div className="wrap-input100">
                    <input
                      className="input100"
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <span className="focus-input100" />
                    {/* hover on the eye icon to preview password */}
                    <span
                      className="symbol-input100"
                      onMouseEnter={() => setShowPassword(true)}
                      onMouseLeave={() => setShowPassword(false)}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </span>
                  </div>

                  {error && <span style={{ color: "red" }}>{error}</span>}

                  <div className="container-login100-form-btn">
                    <button
                      className="login100-form-btn"
                      onClick={handleSubmit}
                      disabled={isLoading}
                      style={{
                        opacity: isLoading ? 0.5 : 1,
                        backgroundColor: isLoading ? "#ccc" : undefined,
                      }}
                    >
                      {isLoading ? "Logging in…" : "Login"}
                    </button>
                  </div>

                  <div className="text-center p-t-12">
                    <span className="txt1">Forgot </span>
                    <button
                      className="txt2"
                      style={{ backgroundColor: "transparent" }}
                      onClick={() => setForgotOpen(true)}
                    >
                      Username / Password?
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <ForgotCredentialsModal open={forgotOpen}  onClose={() => setForgotOpen(false)} />
      <SupportModal           open={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  );
}

export default Login;